import { Color } from '@persona-id/design-tokens';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSITIONS } from 'shared/styles/variables';
import styled from 'styled-components';

import BarcodeHelp from 'images/inquiry/barcode-help.svg';
import IdBackHelp from 'images/inquiry/id-back-help.svg';
import IdFrontHelp from 'images/inquiry/id-front-help.svg';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import GovernmentIdHelpModal from 'inquiry/components/GovernmentIdHelpModal';
import ScannerOverlay from 'inquiry/components/ScannerOverlay';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import type {
  AutoCapturePillPassthroughProps,
  ScannerHelpModalPassthroughProps,
  ScannerOverlayPassthroughProps,
} from 'inquiry/lib/types';
import BarcodeScanner from 'inquiry/scanners/BarcodeScanner';
import GovernmentIdMicroblinkScanner from 'inquiry/scanners/GovernmentIdMicroblinkScanner';
import GovernmentIdScanner from 'inquiry/scanners/GovernmentIdScanner';
import type FrameTrigger from 'lib/clients/FrameTrigger';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { getCaptureFrameLabel, tForGovernmentId } from 'lib/governmentId';
import useToggle from 'lib/hooks/useToggle';
import { colorCss } from 'lib/inquiryStyles';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import {
  AutoCaptureConfig,
  GovernmentIdOverlay,
  GovernmentIdSide,
} from 'lib/interfaces/verificationConfig';
import { supportsInputCapture } from 'lib/utils/device';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { CaptureMethod } from 'verify/models/verification';
import { SelectedCamera } from 'verify/scanners/GovernmentIdMicroblinkScanner/MicroblinkCameraUtils';
import { Z_INDICES } from 'verify/styles/variables';

const AutoCaptureCircle = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background-color: currentcolor;
  margin-right: 4px;
`;

const AutoCapturePill = styled.div<{
  enabled: boolean;
  visible: boolean;
}>`
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 1000px;
  color: ${({ enabled }) => (enabled ? Color.PrimaryWhite : Color.CoreSlate500)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 150%;
  max-width: 50%;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  padding: 4px 8px;
  text-align: center;
  transition: opacity ${TRANSITIONS.fast};
  z-index: ${Z_INDICES.footer};
`;

// this is used to wrap TitlePositioner so we don't have to pass renderTitle all the way to BaseScanner
// The other scanner elements are wrapped inside of BaseScanner
const MAX_INLINE_COLUMN_WIDTH = 450;
const TitleCenterer = styled.div`
  display: flex;
  justify-content: center;
`;
const VideoWrapperColumnSizer = styled.div<{ restrictInlineColumnWidth?: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${({ restrictInlineColumnWidth }) =>
    restrictInlineColumnWidth ? `${MAX_INLINE_COLUMN_WIDTH}px` : null};
`;

const TitlePositioner = styled.div`
  position: absolute;

  // matches default top margin on titles for other government id screen
  top: 72px;

  // matches margins of capture window
  left: 10%;
  right: 10%;

  // ignore SST colors and always white text color
  * {
    color: ${Color.PrimaryWhite} !important;
  }

  z-index: ${Z_INDICES.navbar};
`;

interface InquiryGovernmentIdCaptureViewProps {
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  localizations: StepGovernmentIdConfig['localizations']['capture-page'];
  featureFlags: FeatureFlagsMap;
  governmentIdClass: GovernmentIdClass;
  autoCaptureConfig: AutoCaptureConfig;
  handleCapture: (
    imageDataUris: string[],
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData,
  ) => Promise<void>;
  handleError: (error: Error | string, state: 'load' | 'capture') => void;
  requiresSide: GovernmentIdSide;
  captureOverlay?: GovernmentIdOverlay;
  shouldUseMicroblink: boolean;
  videoConstraints: MediaTrackConstraints;
  webCameraManualCaptureDelayMs: number | null;
  barcodeCameraManualCaptureDelayMs: number | null;
  isRetake?: boolean;
  handleCameraChange: (selectedCamera: SelectedCamera) => void;
  selectedCamera?: string;
  cameraSelectOptions: SelectedCamera[];
  shouldDisplayCameraSelector: boolean;
  requireFullBack?: boolean;
  allowIncompleteBarcode?: boolean;
  uiReady: boolean;
  videoReady: boolean;
}

export default function InquiryGovernmentIdCaptureViewRedesign({
  assets,
  localizations,
  featureFlags,
  governmentIdClass,
  autoCaptureConfig,
  handleCapture,
  handleError,
  requiresSide,
  captureOverlay,
  shouldUseMicroblink,
  videoConstraints,
  webCameraManualCaptureDelayMs,
  barcodeCameraManualCaptureDelayMs,
  isRetake,
  handleCameraChange,
  selectedCamera,
  cameraSelectOptions,
  shouldDisplayCameraSelector,
  requireFullBack,
  allowIncompleteBarcode,
  uiReady,
  videoReady,
}: InquiryGovernmentIdCaptureViewProps) {
  const [t] = useTranslation();

  // if title is present there is no room to show the prompt, so we should hide it instead of specifying a config for it
  // we can't use empty prompt-override config to do this because it's empty by default so we fallback to JSON translations
  const prompt = localizations['title']
    ? null
    : localizations['prompt-override'] ||
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      tForGovernmentId(t, ['government-id.capture.prompt'], null, governmentIdClass, requiresSide);

  // prompt is handled separately from hint in redesign v2
  // prompt always lives above the frame, while hint will override prompt
  // only on barcode (otherwise it lives inside the frame)
  const [hint, setHint] = useState<string | null>(null);

  const handleTrigger = useCallback(
    (
      trigger: FrameTrigger<
        unknown,
        unknown,
        StepGovernmentIdConfig['localizations']['capture-page']
      >,
    ) => {
      const newHint = trigger.getPrompt({ t, localizations });
      setHint(newHint);
    },
    [t, localizations],
  );

  const [overlayReady, { enable: setOverlayReady }] = useToggle(
    // when new perf flag is enabled overlay renders fully expanded since animation plays on separate route
    featureFlags['inquiry:enable-experimental-govid-perf-improvements'] &&
      featureFlags['inquiry:enable-experimental-govid-perf-improvements-percentage'],
  );

  const {
    base: baseStyles,
    buttons,
    captureFeedBox,
    captureHint,
    captureHintIcon,
    captureButtons,
    captureHelpButtonIcon,
    captureOverlayHeaderButton,
  } = useStepGovernmentIdStyleContext();

  const helpButtonIconColor = colorCss(captureHelpButtonIcon?.stroke?.color) ?? Color.PrimaryWhite;

  const overlayRef = useRef<HTMLDivElement>();
  const overlayCaptureWindowRef = useRef<HTMLDivElement>();
  const previewViewfinderRef = useRef<HTMLDivElement>();

  const autoCaptureRuleSetSupported = autoCaptureConfig?.['rule-sets']?.length > 0;
  /**
   * Whether or not autocapture is supported for the current ID side.
   */
  const autoCaptureEnabled =
    (autoCaptureConfig == null || autoCaptureRuleSetSupported) &&
    (shouldUseMicroblink ||
      ![GovernmentIdSide.Back, GovernmentIdSide.PassportSignature].includes(requiresSide));

  /**
   * Whether or not the autocapture models have loaded. Solely used for
   * adjusting the autocapture UI indicators.
   */
  const [autoCaptureReady, { enable: setAutoCaptureReady }] = useToggle(false);

  const isShortBarcodeOverlay = requiresSide === GovernmentIdSide.BarcodePdf417;
  const showIdFrontHelpModalButton = requiresSide === GovernmentIdSide.Front;
  const showIdBackHelpModalButton = requiresSide === GovernmentIdSide.Back;

  const showHelpModalButton =
    isShortBarcodeOverlay || showIdFrontHelpModalButton || showIdBackHelpModalButton;

  const isMobile = supportsInputCapture();

  const renderOverlay = ({
    hasDetectedQuad,
    isCameraReady,
    renderUnderOverlayComponents,
    mirrored,
  }: ScannerOverlayPassthroughProps) => {
    return (
      <ScannerOverlay
        styles={{
          base: baseStyles,
          captureBox: captureFeedBox,
          captureHintIcon,
          captureOverlayHeaderButton: captureOverlayHeaderButton,
          hint: captureHint?.textStyle,
        }}
        assets={assets}
        hasDetectedQuad={hasDetectedQuad}
        isCameraReady={isCameraReady}
        isShortBarcodeOverlay={isShortBarcodeOverlay}
        governmentIdClass={governmentIdClass}
        requiresSide={requiresSide}
        captureOverlay={captureOverlay}
        mirrored={mirrored}
        captureFrameLabel={getCaptureFrameLabel(localizations, governmentIdClass, requiresSide)}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        prompt={prompt}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        hint={hint}
        // passport signature doesn't support autocapture - don't show scanning animation
        scanning={autoCaptureEnabled && autoCaptureReady}
        collapsed={false}
        // Skip animation if animation has already played. For example, if
        // MicroblinkScanner errors and we fall back to GovernmentIdScanner, we
        // don't want to replay the animation.
        // NOTE: The animation will still restart if the error happens during
        // the animation.
        expandOnMount={!isRetake && !overlayReady}
        onReady={setOverlayReady}
        overlayRef={overlayRef}
        overlayCaptureWindowRef={overlayCaptureWindowRef}
        previewViewfinderRef={previewViewfinderRef}
        renderUnderOverlayComponents={renderUnderOverlayComponents}
        videoReady={videoReady}
      />
    );
  };

  const renderTitle = () =>
    localizations['title'] ? (
      <TitleCenterer>
        <VideoWrapperColumnSizer restrictInlineColumnWidth={HolderClient.isInline}>
          <TitlePositioner>
            <ThemedTitle styles={baseStyles?.titleStyle}>{localizations['title']}</ThemedTitle>
          </TitlePositioner>
        </VideoWrapperColumnSizer>
      </TitleCenterer>
    ) : null;

  const renderAutoCapturePill = ({ isCameraReady }: AutoCapturePillPassthroughProps) => (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <AutoCapturePill enabled={autoCaptureEnabled && autoCaptureReady} visible={isCameraReady}>
      <AutoCaptureCircle />
      <span>
        {autoCaptureEnabled && autoCaptureReady
          ? localizations['auto-capture-on']
          : localizations['auto-capture-off']}
      </span>
    </AutoCapturePill>
  );
  const renderHelpModal = ({ isOpen, onClose }: ScannerHelpModalPassthroughProps) => {
    if (isShortBarcodeOverlay) {
      return (
        <GovernmentIdHelpModal
          isOpen={isOpen}
          onClose={onClose}
          title={localizations['barcode-help-modal-title']}
          prompt='' // TODO(tianen) Add prompt text
          warning={localizations['barcode-help-modal-warning']}
          pictograph={
            <CustomAssetWithFallback
              customImage={assets['barcode-help-modal-pictograph']}
              alt='barcode help modal pictograph'
            >
              <BarcodeHelp />
            </CustomAssetWithFallback>
          }
          hints={localizations['barcode-help-modal-hints']}
          continueBtnLabel={localizations['barcode-help-modal-continue-btn']}
          styles={{ base: baseStyles, buttons, captureHintIcon }}
        />
      );
    } else if (showIdFrontHelpModalButton) {
      return (
        <GovernmentIdHelpModal
          isOpen={isOpen}
          onClose={onClose}
          title={localizations['id-front-help-modal-title']}
          prompt={localizations['id-front-help-modal-prompt']}
          pictograph={
            <CustomAssetWithFallback
              customImage={assets['id-front-help-modal-pictograph']}
              alt='ID front help modal pictograph'
            >
              <IdFrontHelp />
            </CustomAssetWithFallback>
          }
          hints={
            isMobile
              ? localizations['id-front-help-modal-hints-mobile']
              : localizations['id-front-help-modal-hints-desktop']
          }
          continueBtnLabel={localizations['id-front-help-modal-continue-btn']}
          styles={{ base: baseStyles, buttons, captureHintIcon }}
        />
      );
    } else if (showIdBackHelpModalButton) {
      return (
        <GovernmentIdHelpModal
          isOpen={isOpen}
          onClose={onClose}
          title={localizations['id-back-help-modal-title']}
          prompt={localizations['id-back-help-modal-prompt']}
          pictograph={
            <CustomAssetWithFallback
              customImage={assets['id-back-help-modal-pictograph']}
              alt='ID back help modal pictograph'
            >
              <IdBackHelp />
            </CustomAssetWithFallback>
          }
          hints={
            isMobile
              ? localizations['id-back-help-modal-hints-mobile']
              : localizations['id-back-help-modal-hints-desktop']
          }
          continueBtnLabel={localizations['id-back-help-modal-continue-btn']}
          styles={{ base: baseStyles, buttons, captureHintIcon }}
        />
      );
    }
  };

  const renderScanner = () => {
    if (autoCaptureEnabled && shouldUseMicroblink) {
      const manualCaptureDelayMs =
        requiresSide === GovernmentIdSide.BarcodePdf417
          ? barcodeCameraManualCaptureDelayMs
          : webCameraManualCaptureDelayMs;
      return (
        <GovernmentIdMicroblinkScanner
          setAutoCaptureReady={setAutoCaptureReady}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          delayUntilManualCaptureMs={manualCaptureDelayMs}
          handleCapture={handleCapture}
          handleError={handleError}
          handleTrigger={handleTrigger}
          labelTakePhoto={t('government-id.capture.btn-take-photo')}
          renderOverlay={renderOverlay}
          requiresSide={requiresSide}
          governmentIdClass={governmentIdClass}
          videoConstraints={videoConstraints}
          shouldDisplayCameraSelector={shouldDisplayCameraSelector}
          cameraSelectOptions={cameraSelectOptions}
          selectedCamera={selectedCamera}
          handleCameraChange={handleCameraChange}
          overlayReady={overlayReady}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          overlayRef={overlayRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          overlayCaptureWindowRef={overlayCaptureWindowRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          previewViewfinderRef={previewViewfinderRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          requireFullBack={requireFullBack}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          allowIncompleteBarcode={allowIncompleteBarcode}
          isShortBarcodeOverlay={isShortBarcodeOverlay}
          submitButtonStyles={captureButtons?.submit}
          showHelpButton={showHelpModalButton}
          helpButtonIconColor={helpButtonIconColor}
          helpButtonLabel={localizations['btn-help']}
          renderAutoCapturePill={renderAutoCapturePill}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          renderHelpModal={renderHelpModal}
          uiReady={uiReady}
          videoReady={videoReady}
          assets={assets}
          captureOverlay={captureOverlay}
          captureFrameLabel={getCaptureFrameLabel(localizations, governmentIdClass, requiresSide)}
          hint={hint}
          prompt={prompt}
        />
      );
    } else if (requiresSide === GovernmentIdSide.BarcodePdf417) {
      return (
        <BarcodeScanner
          setAutoCaptureReady={setAutoCaptureReady}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          delayUntilManualCaptureMs={barcodeCameraManualCaptureDelayMs}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCapture={handleCapture}
          handleError={handleError}
          handleTrigger={handleTrigger}
          labelTakePhoto={t('government-id.capture.btn-take-photo')}
          renderOverlay={renderOverlay}
          requiresSide={requiresSide}
          videoConstraints={videoConstraints}
          shouldDisplayCameraSelector={shouldDisplayCameraSelector}
          cameraSelectOptions={cameraSelectOptions}
          selectedCamera={selectedCamera}
          handleCameraChange={handleCameraChange}
          overlayReady={overlayReady}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          overlayRef={overlayRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          overlayCaptureWindowRef={overlayCaptureWindowRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          previewViewfinderRef={previewViewfinderRef}
          isShortBarcodeOverlay={isShortBarcodeOverlay}
          submitButtonStyles={captureButtons?.submit}
          showHelpButton={showHelpModalButton}
          helpButtonIconColor={helpButtonIconColor}
          helpButtonLabel={localizations['btn-help']}
          renderAutoCapturePill={renderAutoCapturePill}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          renderHelpModal={renderHelpModal}
          uiReady={uiReady}
          videoReady={videoReady}
          assets={assets}
          governmentIdClass={governmentIdClass}
          captureOverlay={captureOverlay}
          captureFrameLabel={getCaptureFrameLabel(localizations, governmentIdClass, requiresSide)}
          hint={hint}
          prompt={prompt}
        />
      );
    } else {
      return (
        <GovernmentIdScanner
          autoCaptureEnabled={autoCaptureEnabled}
          setAutoCaptureReady={setAutoCaptureReady}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          delayUntilManualCaptureMs={webCameraManualCaptureDelayMs}
          handleCapture={handleCapture}
          handleTrigger={handleTrigger}
          handleError={handleError}
          isPassport={governmentIdClass === 'pp' || governmentIdClass === 'ipp'}
          labelTakePhoto={localizations['btn-take-photo']}
          renderOverlay={renderOverlay}
          requiresSide={requiresSide}
          videoConstraints={videoConstraints}
          shouldDisplayCameraSelector={shouldDisplayCameraSelector}
          cameraSelectOptions={cameraSelectOptions}
          selectedCamera={selectedCamera}
          handleCameraChange={handleCameraChange}
          overlayReady={overlayReady}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          overlayRef={overlayRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          overlayCaptureWindowRef={overlayCaptureWindowRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          previewViewfinderRef={previewViewfinderRef}
          submitButtonStyles={captureButtons?.submit}
          showHelpButton={showHelpModalButton}
          helpButtonIconColor={helpButtonIconColor}
          helpButtonLabel={localizations['btn-help']}
          renderAutoCapturePill={renderAutoCapturePill}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          renderHelpModal={renderHelpModal}
          uiReady={uiReady}
          videoReady={videoReady}
          assets={assets}
          governmentIdClass={governmentIdClass}
          captureOverlay={captureOverlay}
          captureFrameLabel={getCaptureFrameLabel(localizations, governmentIdClass, requiresSide)}
          hint={hint}
          prompt={prompt}
        />
      );
    }
  };

  return (
    <>
      {renderTitle()}
      {renderScanner()}
    </>
  );
}
